import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Thread pieces of the inbox (design/components/message-bubble.html).
// MessageBubble distinguishes sender by LABEL + side + treatment (never colour
// alone): user (grey, left), bot (bordered, right), agent (brand blue, right).
// SystemEvent marks transitions without looking like a message. InternalNote is
// team-only and must NOT resemble an outgoing bubble (risk of sending it).
// The "Bot" label is internal-only — never reveal the bot to the client.

export type MessageSender = 'user' | 'bot' | 'agent'

export interface MessageBubbleProps {
  sender: MessageSender
  author?: string
  /** Clock time of the turn. Optional: a bubble that is not a real message (a
   *  worked EXAMPLE of a conversation) has no hour to show, and inventing one
   *  would be the only false thing on the screen. Absent = the label keeps its
   *  sender words and drops the ` · time` half. */
  time?: string
  children: ReactNode
  className?: string
}

/** Thread bubble distinguishing sender by label + side + treatment, never
 *  colour alone: user grey/left, bot bordered/right, agent brand-blue/right.
 *  The "Bot" label is internal-only — the client never sees it. */
export function MessageBubble({ sender, author, time, children, className }: MessageBubbleProps) {
  const { t } = useTranslation('components')
  const outgoing = sender !== 'user'
  const at = time === undefined ? null : <> · {time}</>
  const label =
    sender === 'user' ? (
      <>
        <b className="font-semibold text-text">{author}</b>
        {at}
      </>
    ) : sender === 'bot' ? (
      <>
        {t('ui.messageThread.botLabel')}
        {at}
      </>
    ) : (
      <>
        {author} · {t('ui.messageThread.agentLabel')}
        {at}
      </>
    )

  return (
    <div className={cn('mb-3.5 max-w-[80%]', outgoing ? 'ml-auto' : 'mr-auto', className)}>
      <div className={cn('mx-1 mb-1 text-[10px] text-text-2', outgoing && 'text-right')}>
        {label}
      </div>
      <div
        className={cn(
          'inline-block rounded-[14px] px-3 py-[9px] text-left text-[12.5px] leading-relaxed [overflow-wrap:anywhere]',
          sender === 'user' && 'rounded-bl-[5px] bg-field text-text',
          sender === 'bot' && 'rounded-br-[5px] border border-line bg-surface text-text',
          sender === 'agent' &&
            'rounded-br-[5px] bg-[linear-gradient(140deg,#1d4fd1,#1538b8)] text-white',
        )}
      >
        {children}
      </div>
    </div>
  )
}

function WarnIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10.3 3.1 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.1a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}
function HandoffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7" />
    </svg>
  )
}
function ReopenIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 4v4h4" />
    </svg>
  )
}

const eventCfg = {
  sensitive: { color: 'text-sens', Icon: WarnIcon },
  handoff: { color: 'text-text-2', Icon: HandoffIcon },
  resolved: { color: 'text-ok', Icon: CheckIcon },
  reopened: { color: 'text-text-2', Icon: ReopenIcon },
} as const

export interface SystemEventProps {
  kind: 'sensitive' | 'handoff' | 'resolved' | 'reopened'
  text: string
}

/** Inline divider marking a conversation transition (sensitive detected,
 *  handoff, resolved, reopened) — deliberately unlike a message bubble. */
export function SystemEvent({ kind, text }: SystemEventProps) {
  const cfg = eventCfg[kind]
  return (
    <div className="my-1.5 mb-4 flex items-center gap-[9px] before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line">
      <span
        className={cn(
          'flex items-center gap-1.5 text-[10.5px] font-medium [&_svg]:size-3',
          cfg.color,
        )}
      >
        <cfg.Icon />
        {text}
      </span>
    </div>
  )
}

export interface InternalNoteProps {
  author: string
  time: string
  children: ReactNode
}

/** Team-only note in the thread, amber and labelled so it can NEVER be
 *  mistaken for an outgoing bubble (the risk is sending it to the client). */
export function InternalNote({ author, time, children }: InternalNoteProps) {
  const { t } = useTranslation('components')
  return (
    <div className="mb-4 ml-auto max-w-[80%] rounded-xl border border-[rgba(255,104,0,0.28)] bg-warn-bg px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.04em] text-warn [&_svg]:size-[11px]">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
        </svg>
        {t('ui.messageThread.internalNote')}
      </div>
      <div className="mt-1.5 text-[12px] leading-normal text-text">{children}</div>
      <div className="mt-1.5 text-[9.5px] text-text-2">
        {t('ui.messageThread.teamOnly')} · {author} · {time}
      </div>
    </div>
  )
}

export interface ServiceWindowBannerProps {
  hoursLeft: number
}

export function ServiceWindowBanner({ hoursLeft }: ServiceWindowBannerProps) {
  const { t } = useTranslation('components')
  const open = hoursLeft > 0
  return (
    <div className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3.5 py-[9px] text-[10.5px] text-text-2 [&_svg]:size-3.5">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={cn('flex-none', open ? 'text-ok' : 'text-warn')}
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span>
        {open ? (
          <>
            <b className="font-semibold text-text">{t('ui.messageThread.windowOpenTitle')}</b> ·{' '}
            {t('ui.messageThread.windowOpenDetail', { count: hoursLeft })}
          </>
        ) : (
          <>
            <b className="font-semibold text-text">{t('ui.messageThread.windowClosedTitle')}</b> ·{' '}
            {t('ui.messageThread.windowClosedDetail')}
          </>
        )}
      </span>
    </div>
  )
}

/** Ruled date divider between thread days; the caller formats the label. */
export function DaySeparator({ label }: { label: string }) {
  return (
    <div className="my-2 mb-4 flex items-center gap-3 before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line">
      <span className="text-[10.5px] font-semibold text-text-2">{label}</span>
    </div>
  )
}

export interface MessageThreadProps {
  children: ReactNode
  /** Accessible name for the conversation log. */
  label?: string
  className?: string
}

/** Conversation container: role=log with aria-live additions so new bubbles
 *  are announced. Children compose MessageBubble / SystemEvent / InternalNote /
 *  DaySeparator; scrolling belongs to the parent. */
export function MessageThread({ children, label, className }: MessageThreadProps) {
  const { t } = useTranslation('components')
  return (
    <div
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label={label ?? t('ui.conversationThread')}
      className={cn(
        'flex flex-col rounded-lg border border-line bg-surface-2 px-5 pb-2 pt-[18px]',
        className,
      )}
    >
      {children}
    </div>
  )
}
